import {
  DataGridPro,
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterInputValueProps,
  GridFilterModel,
  GridPaginationModel,
  GridRowModel,
  GridSortItem,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import combinedQuery from 'graphql-combine-query'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Query_Root, User } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { AddKapProgramToolbar } from 'src/screens/kap/index/add-program/AddKapProgramToolbar'
import { queryAllGfchCantonsResponsibles, queryKapProgramsDates } from 'src/screens/kap/index/kapProgramGridQueries'
import { fetchKapRows } from 'src/screens/kap/index/KapProgramGridUtils'
import { OverflowTooltip } from 'src/screens/kap/index/tooltip/OverflowTooltip'
import {
  fetchMilestoneConfigsForProcessAndType,
  getMilestoneTitle,
} from 'src/screens/shared/implementation/details/milestone/utils/MilestoneUtils'
import { PermissionService } from 'src/service/security/PermissionService'
import { CANTONS, KAP_MODULES, PROCESS, PROGRAM_STATUSES } from 'src/shared/constants/constants'
import { MILESTONE_TYPE } from 'src/shared/constants/milestone-constants'
import { Option } from 'src/shared/form/control'
import { AutoCompleteFilterInput } from 'src/shared/form/grid/filter/AutoCompleteFilterInput'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import {
  autoCompleteContainsArrayFilterOperator,
  getGridStringsOperatorsIgnoreAccents,
} from 'src/shared/grid/DataGridFilters'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { useLocalStorage } from 'src/shared/utils/hooks/local-storage-hooks'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { STORAGE_KEY } from 'src/shared/utils/StorageUtils'
import { Utils } from 'src/shared/utils/Utils'
import { UserContext, useUserLocale } from 'src/user/UserContext'
import styled from 'styled-components'
import { useClient } from 'urql'

const DataGridProStyled = styled(DataGridPro)`
  & .MuiDataGrid-row {
    &:hover {
      cursor: pointer;
    }
  }
`

interface Props {
  addProgram: () => void
}

const PAGE_SIZE = 10

type KapProgramCockpitTableSettings = {
  sort?: GridSortItem
  filtering?: GridFilterModel
  rowsPerPage?: number
}

export const KapProgramCockpitGrid = ({ addProgram }: Props) => {
  const { getMessage } = useMessageSource()
  const gridRef = useGridApiRef()
  const gridTranslations = useGridTranslateHook()
  const notificationService = useNotificationService()
  const urqlClient = useClient()
  const language = useUserLocale()
  const navigate = useDelayedNavigate()

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState<GridRowModel[]>([])
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [gfchCantonResponsibles, setGfchCantonResponsibles] = useState<User[]>([])
  const [startDateYears, setStartDateYears] = useState<string[]>([])
  const [endDateYears, setEndDateYears] = useState<string[]>([])

  const { user } = useContext(UserContext)
  const globalRoles = user.roles
  const canCreateKapProgram = PermissionService.permissionToCreateKapProgram(globalRoles)
  const isGfsUser = Utils.isInternalRole(globalRoles)

  const [tableSettings, setTableSettings] = useLocalStorage<KapProgramCockpitTableSettings>(
    STORAGE_KEY._KAP_STORAGE_V2,
    {
      rowsPerPage: PAGE_SIZE,
    },
  )

  const sortModel: GridSortModel = useMemo(() => {
    return tableSettings?.sort ? [tableSettings?.sort] : []
  }, [tableSettings?.sort])

  const paginationModel: GridPaginationModel = useMemo(
    () => ({
      page: currentPage,
      pageSize: tableSettings?.rowsPerPage ?? PAGE_SIZE,
    }),
    [tableSettings?.rowsPerPage, currentPage],
  )

  const handleRowClick = (params: GridRowModel) => {
    const programId = params?.id
    navigate(ROUTES.KapDetailsRoot.nested.BasicInformation.params({ programId }))
  }

  const autoCompleteEqualsFilterOperator = (options: Option[], label: string, placeholder: string) =>
    getGridStringOperators()
      .filter((operator) => operator.value === 'equals')
      .map((operator) => ({
        ...operator,
        InputComponent: (gridFilterProps: GridFilterInputValueProps) => (
          <AutoCompleteFilterInput {...gridFilterProps} options={options} label={label} placeholder={placeholder} />
        ),
      }))

  const autoCompleteIsFromToFilterOperator = useCallback(
    (options: Option[], label: string, placeholder: string) =>
      getGridDateOperators()
        .filter((operator) => ['is', 'onOrAfter', 'onOrBefore'].some((item) => item.includes(operator.value)))
        .map((operator) => ({
          ...operator,
          InputComponent: (gridFilterProps: GridFilterInputValueProps) => (
            <AutoCompleteFilterInput {...gridFilterProps} options={options} label={label} placeholder={placeholder} />
          ),
        })),
    [],
  )

  const milestoneTypeOptions = useMemo(
    () =>
      Object.values(MILESTONE_TYPE).map((milestoneType) => {
        return { label: getMessage(`label.milestone.type.${milestoneType}`), value: milestoneType }
      }),
    [getMessage],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        flex: 0.6,
        headerName: getMessage('label.program.id'),
        filterOperators: getGridStringOperators().filter((x) => x.value === 'equals'),
        renderCell: ({ row }) => `${getMessage(`label.program.id.character`)}${row.id}`,
      },
      {
        field: 'short_title',
        flex: 2,
        headerName: getMessage('label.short.title'),
        filterOperators: getGridStringsOperatorsIgnoreAccents().filter((x) => x.value === 'contains'),
      },
      {
        field: 'status',
        flex: 0.5,
        headerName: getMessage('label.project.status'),
        filterOperators: autoCompleteEqualsFilterOperator(
          PROGRAM_STATUSES.map((x) => ({
            label: getMessage(`label.project.status.${x}`),
            value: x,
          })),
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
        valueGetter: (value: any) => {
          return value?.value ?? ''
        },
        renderCell: (params) => {
          return params.row.status.label ?? ''
        },
      },
      {
        field: 'canton_code',
        flex: 0.5,
        headerName: getMessage('label.canton'),
        filterOperators: autoCompleteEqualsFilterOperator(
          Object.keys(CANTONS).map((x) => ({
            label: x,
            value: x,
          })),
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
      },
      {
        field: 'start_date',
        headerName: getMessage('label.start.date'),
        flex: 1,
        type: 'date',
        valueGetter: (value) => value && new Date(value),
        renderCell: ({ value }) =>
          value && DateUtils.parseDateGraphQL(DateUtils.serializeGraphQLDate(value)).getFullYear(),
        filterOperators: autoCompleteIsFromToFilterOperator(
          startDateYears.map((x) => ({
            label: x ? DateUtils.parseDateGraphQL(x).getFullYear().toString() : getMessage('label.field.not.available'),
            value: x,
          })),
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
      },
      {
        field: 'end_date',
        headerName: getMessage('label.end.date'),
        flex: 1,
        type: 'date',
        valueGetter: (value) => value && new Date(value),
        renderCell: ({ value }) =>
          value && DateUtils.parseDateGraphQL(DateUtils.serializeGraphQLDate(value)).getFullYear(),
        filterOperators: autoCompleteIsFromToFilterOperator(
          endDateYears.map((x) => ({
            label: x ? DateUtils.parseDateGraphQL(x).getFullYear().toString() : getMessage('label.field.not.available'),
            value: x,
          })),
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
      },
      {
        field: 'next_milestone',
        headerName: getMessage('label.next.milestone'),
        flex: 1,
        sortable: false,
        filterOperators: autoCompleteEqualsFilterOperator(
          milestoneTypeOptions,
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
        ),
        valueGetter: (value: any) => {
          return value?.value ?? ''
        },
        renderCell: (params) => {
          return params.row.next_milestone.label ?? ''
        },
      },
      {
        field: 'gfch_responsible',
        flex: 1.5,
        headerName: getMessage('label.kap.program.gfch.responsible'),
        sortable: false,
        filterOperators: autoCompleteContainsArrayFilterOperator(
          gfchCantonResponsibles.map((resp) => ({
            label: `${resp.first_name} ${resp.last_name}`,
            value: `${resp.first_name} ${resp.last_name}`,
          })),
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
          // NOTE: KBL 2025-05-21 the label is kept for backwards compatible
          getMessage('label.grid.equals'),
        ),
        valueGetter: (value: any) => {
          return value?.users?.map((item: any) => `${item.first_name} ${item.last_name}`) ?? []
        },
        renderCell: (params) => {
          return <OverflowTooltip>{params.row.gfch_responsible}</OverflowTooltip>
        },
      },
      {
        field: 'modules',
        flex: 0.5,
        headerName: getMessage('label.modules'),
        filterOperators: autoCompleteContainsArrayFilterOperator(
          KAP_MODULES.map((x) => ({
            label: getMessage(`label.module.${x}`),
            value: x,
          })),
          getMessage('label.grid.value'),
          getMessage('label.grid.filter.value'),
          // NOTE: KBL 2025-05-21 the label is kept for backwards compatible
          getMessage('label.grid.equals'),
        ),
        sortComparator: (v1, v2) => {
          const a = Array.isArray(v1) ? v1.join(', ') : ''
          const b = Array.isArray(v2) ? v2.join(', ') : ''
          return a.localeCompare(b)
        },
        valueGetter: (value: any) => {
          return value.map((item: any) => item.value) ?? []
        },
        renderCell: (params) => {
          return params.row.modules?.map((item: any) => item.label)?.join(', ') ?? ''
        },
      },
    ],
    [
      autoCompleteIsFromToFilterOperator,
      endDateYears,
      getMessage,
      gfchCantonResponsibles,
      milestoneTypeOptions,
      startDateYears,
    ],
  )

  const fetchRows = useCallback(async () => {
    setLoading(true)
    try {
      const { rows } = await fetchKapRows(urqlClient)(isGfsUser)
      const { data: milestoneConfigData } = await fetchMilestoneConfigsForProcessAndType(
        PROCESS.KAP,
        undefined,
        urqlClient,
      )

      const rowsAdapted = rows.map((r) => {
        const programResponsibleUsers =
          gfchCantonResponsibles.filter(
            (responsible) => responsible.gfch_cantonal_responsibility?.includes(r.canton_code),
          ) ?? []

        const noNextMilestoneText = getMessage('label.no.available.milestone')
        const nextMilestoneConfig =
          milestoneConfigData?.milestone_config.filter(
            (i) => i.process === PROCESS.KAP && i.type === r.curr_milestone_type,
          )?.[0] ?? undefined
        const nextMilestoneText = r.curr_milestone_type
          ? `${getMilestoneTitle(nextMilestoneConfig, language)} ${r.curr_milestone_year ?? ''}`
          : noNextMilestoneText
        return {
          ...r,
          status: {
            label: getMessage(`label.project.status.${r.status}`),
            value: r.status,
          },
          modules: r.modules?.map((m) => ({ value: m, label: getMessage(`label.module.${m}`) })) ?? [],
          gfch_responsible: {
            users: programResponsibleUsers,
            usersDisplayText: programResponsibleUsers
              .map((resp: User) => resp.first_name + ' ' + resp.last_name)
              .join(',  '),
          },
          next_milestone: {
            value: r.curr_milestone_type,
            label: nextMilestoneText,
          },
        }
      })
      setRows(rowsAdapted)
    } catch (e) {
      notificationService.operationFailed()
    }
    setLoading(false)
  }, [urqlClient, getMessage, gfchCantonResponsibles, notificationService, isGfsUser])

  const onGridPaginationChange = useCallback(
    (currentPaginationModel: GridPaginationModel) => {
      const currentPage = currentPaginationModel.page
      const currentRowsPerPage = currentPaginationModel.pageSize

      setTableSettings({ ...tableSettings, rowsPerPage: currentRowsPerPage })
      setCurrentPage(currentPage)
    },

    [setTableSettings, tableSettings],
  )

  const onGridFilterModelChange = useCallback(
    (currentGridFilterModel: GridFilterModel) => {
      setTableSettings({ ...tableSettings, filtering: currentGridFilterModel })
    },

    [setTableSettings, tableSettings],
  )

  const onGridSortItemChange = useCallback(
    (currentGridSortItem: GridSortItem) => {
      setTableSettings({ ...tableSettings, sort: currentGridSortItem })
    },
    [setTableSettings, tableSettings],
  )

  useEffect(() => {
    const initData = async () => {
      const { document } = combinedQuery('fetchCantonResponsiblesAndProgramDates')
        .add(queryAllGfchCantonsResponsibles)
        .add(queryKapProgramsDates)

      const { data, error } = await urqlClient
        .query<{
          user: Query_Root['user']
          dossierStartDate: Query_Root['dossier']
          dossierEndDate: Query_Root['dossier']
        }>(document, undefined)
        .toPromise()

      if (error) {
        notificationService.operationFailed()
      } else {
        const startDates = data?.dossierStartDate.map((dossier) => dossier.start_date) ?? []
        const endDates = data?.dossierEndDate.map((dossier) => dossier.end_date) ?? []
        const gfchCantonResponsibleUsers =
          data?.user.filter((user) => user.gfch_cantonal_responsibility.length !== 0) ?? []
        setStartDateYears(startDates)
        setEndDateYears(endDates)
        setGfchCantonResponsibles(gfchCantonResponsibleUsers)
      }
    }
    initData()
  }, [notificationService, urqlClient])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  return (
    <DataGridContainer>
      <DataGridProStyled
        paginationModel={paginationModel}
        filterModel={tableSettings?.filtering}
        sortModel={sortModel}
        rows={rows}
        columns={columns}
        apiRef={gridRef as any}
        localeText={gridTranslations}
        loading={loading}
        onRowClick={(params) => handleRowClick(params)}
        pagination
        disableColumnPinning
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
        onPaginationModelChange={(currentPaginationModel) => onGridPaginationChange(currentPaginationModel)}
        onSortModelChange={(currentSortModel) => onGridSortItemChange(currentSortModel[0])}
        onFilterModelChange={(currentFilterModel) => onGridFilterModelChange(currentFilterModel)}
        columnVisibilityModel={{
          next_milestone: isGfsUser,
        }}
        slots={{
          toolbar: () => (
            <AddKapProgramToolbar
              title={getMessage('label.program.kap.list')}
              addProgram={addProgram}
              canCreateProgram={canCreateKapProgram}
            />
          ),
        }}
      />
    </DataGridContainer>
  )
}
